import { compact, isEmpty, omit, without } from 'lodash-es'
import { nanoid } from 'nanoid'

import { activeLocationsSelector, nextPositionSelector, sortedActiveLocationsSelector } from './selectors'

const generateId = () => nanoid(5)

const isSelected = ({ kind }) => !isEmpty(kind)

const lockAskInviteePosition = (sortedLocations) => {
  const askInvitee = sortedLocations.find(([_id, location]) => (
    location.kind === 'ask_invitee'
  ))
  if (!askInvitee) {
    return sortedLocations
  }
  const unselectedLocation = sortedLocations.find(([_id, location]) => (
    !isSelected(location)
  ))

  return compact([
    ...without(sortedLocations, askInvitee, unselectedLocation),
    askInvitee,
    unselectedLocation
  ])
}

const normalizeOrdering = (state) => {
  const sortedLocations = lockAskInviteePosition(sortedActiveLocationsSelector(state))
  const locations = sortedLocations.reduce((acc, [id, location], index) => (
    { ...acc, [id]: { ...location, position: index } }
  ), {})
  return {
    ...state,
    locations: {
      ...state.locations,
      ...locations
    }
  }
}

const addLocation = (state, { position = nextPositionSelector(state) }) => ({
  ...state,
  locations: {
    ...state.locations,
    [generateId()]: {
      position
    }
  }
})

const initialLocationOptions = {
  location: null,
  additional_info: null,
  phone_number: null,
  hide_location: false
}

const initialLocationPopupState = {
  isOpen: false,
  locationId: null,
  data: {}
}

const convertLocations = (locations) => (
  locations.reduce((acc, location) => (
    { ...acc, [location.id]: location }
  ), {})
)

const initialState = ({
  locations,
  possibleKinds,
  integrations,
  disabledTooltip,
  isDisabled,
  isCollective,
  isPending,
  showIntegrationErrors,
  showLocationVisibilityOptions,
  potentialKind,
  kindName,
  useNewDropdown,
  useNewLocationSelect,
  isNewEventType
}) => {
  const state = {
    locations: convertLocations(locations),
    potentialKind,
    possibleKinds,
    integrations,
    disabledTooltip,
    isDisabled,
    isCollective,
    isPending,
    showIntegrationErrors,
    showLocationVisibilityOptions,
    locationPopup: initialLocationPopupState,
    kindName,
    useNewDropdown,
    useNewLocationSelect,
    isNewEventType
  }
  if (isEmpty(sortedActiveLocationsSelector(state))) {
    return addLocation(state, { position: 0 })
  }

  return state
}

const selectLocation = (state, { locationId, kind }) => {
  const { id, position } = state.locations[locationId]
  return normalizeOrdering({
    ...state,
    locations: {
      ...state.locations,
      [locationId]: {
        ...initialLocationOptions,
        id,
        position,
        kind
      }
    }
  })
}

const removeLocation = (state, { locationId }) => {
  const locationToRemove = state.locations[locationId]
  const newState = locationToRemove.id ? ({
    ...state,
    locations: {
      ...state.locations,
      [locationId]: {
        ...locationToRemove,
        _destroy: true
      }
    }
  }) : ({
    ...state,
    locations: omit(state.locations, locationId)
  })

  if (isEmpty(activeLocationsSelector(newState))) {
    return addLocation(newState, { position: 0 })
  }
  return normalizeOrdering(newState)
}

const changePopupLocation = (state, { kind }) => {
  const editedLocation = state.locations[state.locationPopup.locationId]
  if (kind === editedLocation.kind) {
    return {
      ...state,
      locationPopup: {
        ...state.locationPopup,
        data: editedLocation
      }
    }
  }

  const { id, position } = state.locationPopup.data
  return {
    ...state,
    locationPopup: {
      ...state.locationPopup,
      data: {
        id,
        position,
        kind
      }
    }
  }
}

const changePopupData = (state, { data }) => ({
  ...state,
  locationPopup: {
    ...state.locationPopup,
    data: {
      ...state.locationPopup.data,
      ...data
    }
  }
})

const openPopup = (state, { locationId }) => ({
  ...state,
  locationPopup: {
    ...state.locationPopup,
    locationId,
    isOpen: true,
    data: state.locations[locationId]
  }
})

const dismissPopup = (state) => ({
  ...state,
  locationPopup: initialLocationPopupState
})

const confirmPopup = (state) => {
  const { locationId, data } = state.locationPopup
  const isNewLocation = !state.locations[locationId].id
  return normalizeOrdering({
    ...state,
    locations: {
      ...state.locations,
      [locationId]: {
        ...initialLocationOptions,
        ...data
      }
    },
    locationPopup: initialLocationPopupState,
    lastAddedLocationId: isNewLocation ? locationId : undefined
  })
}

const clearPotentialKind = (state) => ({
  ...state,
  potentialKind: null
})

const updateIntegrations = (state, { integrations }) => ({
  ...state,
  integrations
})


const selectLocationConfiguration = (state, { locationId, locationConfiguration }) => {
  const { id, position } = state.locations[locationId]

  return normalizeOrdering({
    ...state,
    locations: {
      ...state.locations,
      [locationId]: {
        ...initialLocationOptions,
        ...locationConfiguration,
        id,
        position
      }
    }
  })
}

const updateLocations = (state, { locations }) => {
  const newState = {
    ...state,
    locations: convertLocations(locations)
  }

  if (isEmpty(activeLocationsSelector(newState))) {
    return addLocation(newState, { position: 0 })
  }

  return newState
}

const reducer = (state, { type, payload }) => {
  switch (type) {
    case 'SELECT_LOCATION':
      return selectLocation(state, payload)
    case 'REMOVE_LOCATION':
      return removeLocation(state, payload)
    case 'ADD_LOCATION':
      return addLocation(state, payload)
    case 'CHANGE_POPUP_LOCATION':
      return changePopupLocation(state, payload)
    case 'CHANGE_POPUP_DATA':
      return changePopupData(state, payload)
    case 'OPEN_POPUP':
      return openPopup(state, payload)
    case 'DISMISS_POPUP':
      return dismissPopup(state)
    case 'CONFIRM_POPUP':
      return confirmPopup(state)
    case 'CLEAR_POTENTIAL_KIND':
      return clearPotentialKind(state)
    case 'UPDATE_INTEGRATIONS':
      return updateIntegrations(state, payload)
    case 'SELECT_LOCATION_CONFIGURATION':
      return selectLocationConfiguration(state, payload)
    case 'UPDATE_LOCATIONS':
      return updateLocations(state, payload)
    default:
      return state
  }
}

export {
  reducer,
  initialState
}
